import { LinearGradient } from 'expo-linear-gradient'
import React, { useCallback, useState } from 'react'
import { Pressable, View } from 'react-native'

import { UploadAvatarUI } from 'partials/UploadAvatar/subcomponents/UploadAvatarUI'
import { UploadButton } from 'partials/UploadButton'

import { Avatar } from 'widgets/Avatar'
import { baseCrop, defaultAvatarImage } from 'widgets/Avatar/defaultAvatarImage'
import { Box } from 'widgets/Box'
import { TextButton } from 'widgets/Button'
import { Icon } from 'widgets/Icon'
import { ImageCropper } from 'widgets/ImageCropper'
import { Modal } from 'widgets/Modal'
import { Text } from 'widgets/Text'

import { useAvatarUpload } from 'hooks/useAvatarUpload'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFileSelect } from 'hooks/useFileSelect'

import { SelectedFile } from 'types/file'
import { Media, MediaCrop } from 'types/media'

import { ImageContainer, ImageControlsContainer } from './styled'

export type Props = {
  size: number
}

export const UploadAvatar = ({ size }: Props) => {
  const { data: currentUser, update } = useCurrentUser()

  const { id: userId = '', avatar = defaultAvatarImage } = currentUser ?? {
    id: '',
    avatar: defaultAvatarImage,
  }

  const { handleFileSelect, handleReset, selectedFile, selectedFileUrl } = useFileSelect()
  const { mutateAsync: uploadAvatar } = useAvatarUpload()
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [crop, setCrop] = useState<MediaCrop>(baseCrop)

  const onCropChange = (newCrop: MediaCrop) => setCrop(newCrop)

  const handleClose = useCallback(() => {
    if (isSaving) {
      return
    }

    setIsEditing(false)
    setCrop(baseCrop)
    handleReset()
  }, [handleReset, isSaving])

  const uploadAvatarToUser = useCallback(
    async (file: SelectedFile | Media) => {
      setIsSaving(true)
      const uploadedAvatar = await uploadAvatar({
        data: {
          crop,
          file,
          userId,
        },
      })

      await update.mutate({
        data: {
          avatar: uploadedAvatar,
        },
      })

      handleClose()
      setIsSaving(false)
    },
    [crop, handleClose, update, uploadAvatar, userId],
  )

  const handleSave = useCallback(() => {
    uploadAvatarToUser(selectedFile || defaultAvatarImage)
  }, [selectedFile, uploadAvatarToUser])

  const handleAvatarClick = useCallback(() => {
    setIsEditing(true)
  }, [])

  const handleDelete = useCallback(() => {
    uploadAvatarToUser(defaultAvatarImage)
  }, [uploadAvatarToUser])

  return (
    <View>
      <UploadAvatarUI size={size} avatar={avatar} onPress={handleAvatarClick} />

      <Modal
        actionButtonProps={[
          {
            disabled: isSaving,
            onPress: handleClose,
            title: 'Cancel',
            type: 'v2primary',
          },
          {
            disabled: !selectedFile || isSaving,
            onPress: handleSave,
            title: 'Save',
            type: 'v2primary',
          },
        ]}
        isVisible={isEditing}
        onClose={handleClose}
        title='Select Profile Image'
      >
        <ImageContainer
          flex={1}
          margin={{ vertical: 'xl' }}
          align='center'
          justifyContent='space-between'
        >
          {selectedFileUrl ? (
            <ImageCropper
              src={selectedFileUrl}
              circularCrop
              cropSettings={crop}
              onChange={onCropChange}
            />
          ) : (
            <>
              <Avatar width={200} height={200} image={avatar} />
              <ImageControlsContainer flexDirection='row' justifyContent='space-around'>
                <UploadButton
                  fileType='image'
                  onFileSelect={handleFileSelect}
                  title='Change Image'
                />
                {avatar.url !== defaultAvatarImage.url ? (
                  <TextButton type='black' icon='trash' onPress={handleDelete} title='Delete' />
                ) : null}
              </ImageControlsContainer>
            </>
          )}
        </ImageContainer>
      </Modal>
    </View>
  )
}
